/**
 * Stripe Checkout client for the public landing /pricing page.
 *
 * The flow:
 *  1. The page asks the backend whether checkout is configured (`getPaymentConfig`).
 *  2. When the user clicks a paid plan, `createCheckoutSession` POSTs the
 *     selection and we receive a Stripe-hosted URL.
 *  3. The caller redirects the browser to that URL via
 *     `window.location.href = url`.
 *
 * No Stripe.js SDK is loaded — Stripe-hosted Checkout only requires the URL.
 */
import { getApiUrl } from '../config/api';

/**
 * The only plan new customers can buy. Subscribers created against a retired
 * tier keep being billed on their original Stripe Price — nothing in this
 * client can start a checkout for those slugs any more.
 */
export type PlanId = 'annual';

export interface CreateCheckoutSessionInput {
    planId: PlanId;
}

export interface CreateCheckoutSessionResult {
    url: string;
    id: string;
}

/** Live price data fetched from the Stripe Price object — the single source of truth. */
export interface PlanPrice {
    /** Amount in cents (e.g. 500000 = $5,000.00). */
    amount: number;
    /** ISO currency code, lowercase (e.g. "usd"). */
    currency: string;
    /** Pre-formatted display string (e.g. "$5,000"). Use this in the UI. */
    display: string;
    /** Billing interval from the Stripe Price (e.g. "year"). */
    interval: string;
}

export interface PaymentConfig {
    /** Publishable key (safe to expose). Empty string when unset. */
    publishableKey: string;
    /**
     * True when both Stripe keys are configured server-side. The FE uses this
     * to show/hide the pricing section and enable buy buttons.
     */
    enabled: boolean;
    /**
     * Authoritative plan prices fetched from Stripe, keyed by plan slug
     * (e.g. "annual"). Empty when Price IDs are not yet configured.
     */
    plans: Record<string, PlanPrice>;
}

async function readError(res: Response): Promise<string> {
    try {
        const data = await res.json();
        if (data && typeof data.error === 'string') return data.error;
    } catch {
        // fall through
    }
    return `Request failed with status ${res.status}`;
}

export async function getPaymentConfig(): Promise<PaymentConfig> {
    const res = await fetch(getApiUrl('/api/payments/config/'), {
        headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
        throw new Error(await readError(res));
    }
    return res.json();
}

export async function createCheckoutSession(
    input: CreateCheckoutSessionInput,
): Promise<CreateCheckoutSessionResult> {
    const body = {
        planId: input.planId,
        successUrl: `${window.location.origin}/pricing?checkout=success`,
        cancelUrl: `${window.location.origin}/pricing?checkout=cancelled`,
    };
    const res = await fetch(getApiUrl('/api/payments/create-checkout-session/'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        throw new Error(await readError(res));
    }
    return res.json();
}
